import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  LoaderCircle,
  Search,
  ShieldCheck,
  FileText,
  MessageSquareWarning,
  FileDiff,
  RefreshCw,
} from 'lucide-react';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useDashboardCache } from '@/lib/DashboardCacheContext';
import { usePermissions } from '@/lib/usePermissions';
import { useSubmissionFocus } from '@/lib/SubmissionFocusContext';
import { useScanStatus } from '@/lib/ScanStatusContext';
import { computeClearScopeBreakdown, formatClearPercent } from '@/lib/submissionMetrics';

import { timeAgo } from '@/lib/timeAgo';
import { formatInstant } from '@/lib/formatInstant';
import { useDisplayTimezone } from '@/lib/useDisplayTimezone';
import { Telemetry } from '@/lib/telemetry';
import { startTimer, endTimer } from '@/lib/telemetry-timers';
import type { SectionOwnerScanResponse, Submission } from '@/types/api';

type UIState = 'loading' | 'landing' | 'scanning' | 'results';
type DocFilter = 'all' | 'not-clear' | 'has-comments' | 'has-changes';

const PAGE_SIZE = 10;

export function ClearPage() {
  const tz = useDisplayTimezone();
  const { activeProject } = useProject();
  const { data: dashboardData, invalidate: invalidateDashboard } = useDashboardCache();
  const { docSubmissionMap, submissions: focusSubmissions } = useSubmissionFocus();
  const { canRunScans } = usePermissions();
  const scanStatus = useScanStatus();
  const scanBusy = scanStatus.isScanning('section-owners');

  // Page-local sub picker — mirrors Document Readiness. Replaced the
  // global `useSubmissionFilter` dependency on this surface so the Plan
  // tabs share a consistent "operate on a planning sub" model. The
  // global Submission Focus banner is suppressed on this route via
  // FILTERED_ROUTES so the two affordances don't double up.
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [submissionsLoading, setSubmissionsLoading] = useState(true);

  const [uiState, setUiState] = useState<UIState>('loading');
  const [data, setData] = useState<SectionOwnerScanResponse | null>(null);
  const [error, setError] = useState('');

  const [docSearch, setDocSearch] = useState('');
  const [docFilter, setDocFilter] = useState<DocFilter>('all');
  const [docPage, setDocPage] = useState(0);

  const [toast, setToast] = useState('');
  const abortRef = useRef<AbortController | null>(null);

  const projectId = activeProject?.id;

  // Fetch the active-submissions list and pick a default. Same pattern as
  // DocumentReadinessPage — only `isPlanning` subs are eligible.
  useEffect(() => {
    if (!projectId) {
      setSubmissions([]);
      setSubmissionId(null);
      setSubmissionsLoading(false);
      return;
    }
    // Reset selection synchronously on project change — prevents the
    // cache-fetch effect below from firing with a stale (B project,
    // A submissionId) pair while the new submissions list is in flight.
    setSubmissionId(null);
    let cancelled = false;
    setSubmissionsLoading(true);
    api.projects
      .submissions(projectId, true)
      .then((subs) => {
        if (cancelled) return;
        const planning = subs.filter((s) => s.isPlanning);
        setSubmissions(planning);
        if (planning.length > 0) {
          setSubmissionId(planning[0].id);
        } else {
          setSubmissionId(null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSubmissions([]);
        setSubmissionId(null);
      })
      .finally(() => {
        if (!cancelled) setSubmissionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Check for cached results whenever the focused sub changes. Cancelled
  // flag guards against rapid project switches landing late responses
  // from a stale (project, sub) pair on top of newer state.
  useEffect(() => {
    if (!projectId || !submissionId) return;
    let cancelled = false;
    setUiState('loading');
    setError('');
    api.sectionOwners
      .get(projectId, submissionId)
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setUiState('results');
      })
      .catch(() => {
        if (cancelled) return;
        setUiState('landing');
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, submissionId]);

  const startScan = useCallback(() => {
    if (!projectId || !submissionId) return;
    if (!scanStatus.startScan('section-owners')) return;
    setUiState('scanning');
    setError('');
    const controller = new AbortController();
    abortRef.current = controller;

    startTimer('scan_clear');
    Telemetry.clearScanStarted({});

    api.sectionOwners
      .scan(projectId, controller.signal, submissionId)
      .then((result) => {
        setData(result);
        setUiState('results');
        Telemetry.clearScanCompleted({
          annotation_count:
            result.summary.totalUnresolvedComments + result.summary.totalTrackedChanges,
          duration_ms: endTimer('scan_clear'),
        });
        invalidateDashboard();
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          setUiState(data ? 'results' : 'landing');
          return;
        }
        setError(err.message || 'Scan failed');
        setUiState(data ? 'results' : 'landing');
      })
      .finally(() => {
        abortRef.current = null;
        scanStatus.endScan('section-owners');
      });
  }, [projectId, data, submissionId, scanStatus, invalidateDashboard]);

  const cancelScan = () => {
    abortRef.current?.abort();
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const handleOpenDoc = useCallback(
    async (docName: string) => {
      if (!data) return;
      const docId = data.documentMap[docName];
      if (docId) {
        api.documents.openLocal(docId).catch(() => {});
      }
      showToast(`Opening ${docName}...`);
    },
    [data],
  );

  // Set of document IDs belonging to the selected submission (for filtering)
  const submissionDocIds = useMemo(() => {
    if (!docSubmissionMap || !submissionId) return null;
    const ids = new Set<string>();
    for (const [docId, subId] of docSubmissionMap) {
      if (subId === submissionId) ids.add(docId);
    }
    return ids;
  }, [docSubmissionMap, submissionId]);

  // Document clearance filter
  const filteredDocs = useMemo(() => {
    if (!data) return [];
    let items = data.documents ?? [];
    if (submissionDocIds) {
      items = items.filter((d) => submissionDocIds.has(d.documentId));
    }
    if (docFilter === 'not-clear') {
      items = items.filter((d) => !d.isClear);
    } else if (docFilter === 'has-comments') {
      items = items.filter((d) => d.unresolvedComments > 0);
    } else if (docFilter === 'has-changes') {
      items = items.filter((d) => d.trackedChanges > 0);
    }
    if (docSearch.trim()) {
      const q = docSearch.toLowerCase();
      items = items.filter((d) => d.document.toLowerCase().includes(q));
    }
    return items;
  }, [data, docFilter, docSearch, submissionDocIds]);

  // A2F-491: scope breakdown (thisSub / allScopes) for the % Clear summary
  // tile. Uses the dashboard cache's project-wide sectionOwners.documents
  // (which is fetched WITHOUT a submissionId filter) so that "all scopes"
  // reflects the full project. Now always non-null when a submission is
  // focused (which is the only state this page renders results in).
  // A2F-761: middle "this sub + prior" row removed from the rendered tiles;
  // the data layer still computes thisSubAndPrior so it can be reinstated.
  const clearScope = useMemo(
    () =>
      submissionId
        ? computeClearScopeBreakdown(
            dashboardData?.sectionOwners?.documents,
            docSubmissionMap,
            focusSubmissions,
            submissionId,
          )
        : null,
    [submissionId, dashboardData?.sectionOwners?.documents, docSubmissionMap, focusSubmissions],
  );

  const docTotalPages = Math.max(1, Math.ceil(filteredDocs.length / PAGE_SIZE));
  const docPageItems = filteredDocs.slice(docPage * PAGE_SIZE, (docPage + 1) * PAGE_SIZE);

  useEffect(() => {
    setDocPage(0);
  }, [docFilter, docSearch]);

  if (!activeProject) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-400">
            Select a project to check document clearance status.
          </p>
        </div>
      </div>
    );
  }

  // --- No active submissions ---
  if (!submissionsLoading && submissions.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white py-16 text-center">
        <FileText className="mx-auto mb-3 h-10 w-10 text-slate-300" />
        <p className="text-sm font-medium text-slate-500">No active submissions</p>
        <p className="mt-1 text-xs text-slate-400">
          Activate submissions for planning to track document clearance.
        </p>
      </div>
    );
  }

  // --- Loading ---
  if (submissionsLoading || uiState === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <LoaderCircle className="w-6 h-6 text-brand-orange animate-spin" />
        <p className="text-sm text-slate-500">Checking for cached results...</p>
      </div>
    );
  }

  // --- Scanning ---
  if (uiState === 'scanning') {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-4">
        <LoaderCircle className="w-8 h-8 text-brand-orange animate-spin mx-auto" />
        <p className="text-sm text-slate-600">
          Scanning documents for clearance status...
        </p>
        <p className="text-xs text-slate-400">
          Checking documents for comments, tracked changes, and owner sign-off. This is fast — no LLM needed.
        </p>
        <button
          onClick={cancelScan}
          className="text-xs text-slate-500 hover:text-slate-700 underline"
        >
          Cancel
        </button>
      </div>
    );
  }

  // --- Landing ---
  if (uiState === 'landing') {
    return (
      <div className="space-y-6">
        {/* Submission pills + Rescan affordance — visible even before
            first scan so users can pick which sub they're starting on. */}
        <SubmissionPillsRow
          submissions={submissions}
          submissionId={submissionId}
          onPick={setSubmissionId}
          onRescan={startScan}
          canRescan={canRunScans && !!submissionId}
          rescanBusy={scanBusy}
        />
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center space-y-4">
            <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
              Chrona Documents
            </p>
            <h1 className="text-xl font-bold text-brand-charcoal">Document Clearance</h1>
            <p className="text-sm text-slate-500">
              Scan your documents for unresolved comments, tracked changes, and owner sign-off
              status. A document is "clear" when all issues are resolved.
            </p>

            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            <button
              onClick={startScan}
              disabled={!canRunScans || scanBusy || !submissionId}
              title={!canRunScans ? 'Requires Contributor role' : scanBusy ? 'Scan in progress' : undefined}
              className={`inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors ${!canRunScans || scanBusy || !submissionId ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {scanBusy ? 'Scan in progress…' : 'Start Scan'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Results ---
  if (!data) return null;
  const { summary } = data;

  // A2F-595: derive the "this sub" Clear % from the SAME fresh per-doc scan
  // source as the subtitle below, so the headline % is always consistent with
  // the displayed (num/den) and never depends on the dashboard cache's
  // freshness (the old `clearScope.clearance.thisSub` went stale → "—" until a
  // Rescan). scopeLines (thisSubAndPrior / allScopes) still come from clearScope.
  const thisSubClear = {
    num: filteredDocs.filter((d) => d.isClear).length,
    den: filteredDocs.length,
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 bg-slate-800 text-white text-sm rounded-lg shadow-lg animate-fade-in">
          {toast}
        </div>
      )}

      {/* Header */}
      <div>
        <p className="text-[10px] font-semibold text-brand-orange uppercase tracking-widest">
          Chrona Documents
        </p>
        <h1 className="text-xl font-bold text-brand-charcoal">Document Clearance</h1>
      </div>

      {/* Active subs + Rescan — same row layout as Document Readiness. */}
      <SubmissionPillsRow
        submissions={submissions}
        submissionId={submissionId}
        onPick={setSubmissionId}
        onRescan={startScan}
        canRescan={canRunScans && !!submissionId}
        rescanBusy={scanBusy}
      />

      {error && (
        <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Summary cards — A2F-666: "% Signed Off" tile removed; the section
          + document sign-off picture lives on Document Readiness now. The
          % Clear tile keeps its scope breakdown (thisSub / allScopes) so
          users can still see issues inherited from prior subs (A2F-761:
          middle "this sub + prior" row dropped; data shape preserved). */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard
          label="Total Documents"
          value={filteredDocs.length}
          color="slate"
          icon={FileText}
        />
        <SummaryCard
          label="% Clear"
          value={formatClearPercent(thisSubClear)}
          subtitle={` (${thisSubClear.num}/${thisSubClear.den})`}
          color="green"
          icon={ShieldCheck}
          scopeLines={
            clearScope
              ? {
                  thisSubAndPrior: formatClearPercent(clearScope.clearance.thisSubAndPrior),
                  allScopes: formatClearPercent(clearScope.clearance.allScopes),
                }
              : undefined
          }
        />
        <SummaryCard
          label="Unresolved Comments"
          value={filteredDocs.reduce((s, d) => s + d.unresolvedComments, 0)}
          color="orange"
          icon={MessageSquareWarning}
        />
        <SummaryCard
          label="Tracked Changes"
          value={filteredDocs.reduce((s, d) => s + d.trackedChanges, 0)}
          color="orange"
          icon={FileDiff}
        />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search documents..."
            value={docSearch}
            onChange={(e) => setDocSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>
        <select
          value={docFilter}
          onChange={(e) => setDocFilter(e.target.value as DocFilter)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange bg-white"
        >
          <option value="all">All Documents</option>
          <option value="not-clear">Not Clear</option>
          <option value="has-comments">Has Comments</option>
          <option value="has-changes">Has Changes</option>
        </select>
      </div>

      {/* Document Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-4 py-3 font-medium text-slate-600 w-10">Status</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Document</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Unresolved Comments</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Tracked Changes</th>
            </tr>
          </thead>
          <tbody>
            {docPageItems.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-400">
                  No documents match your search.
                </td>
              </tr>
            )}
            {docPageItems.map((doc) => (
              <tr
                key={doc.documentId}
                className="border-b border-slate-50 hover:bg-slate-50/50"
              >
                <td className="px-4 py-3">
                  <StatusBadge status={doc.isClear ? 'clear' : 'not_clear'} feature="clear_document" iconSize="w-3.5 h-3.5" />
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => handleOpenDoc(doc.document)}
                    title={doc.document}
                    className="text-left font-medium text-brand-charcoal hover:text-brand-orange hover:underline"
                  >
                    {doc.document}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <span className={doc.unresolvedComments > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}>
                    {doc.unresolvedComments}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={doc.trackedChanges > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}>
                    {doc.trackedChanges}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {docTotalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>
            Showing {docPage * PAGE_SIZE + 1}&ndash;
            {Math.min((docPage + 1) * PAGE_SIZE, filteredDocs.length)} of {filteredDocs.length}
          </span>
          <div className="flex gap-1">
            <button
              disabled={docPage === 0}
              onClick={() => setDocPage((p) => p - 1)}
              className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={docPage >= docTotalPages - 1}
              onClick={() => setDocPage((p) => p + 1)}
              className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-xs text-slate-400 flex items-center gap-2">
        <span title={formatInstant(data.scannedAt, tz)}>Scanned {timeAgo(data.scannedAt, tz)}</span>
        <span>&middot;</span>
        <span>
          {summary.scannedFiles} file{summary.scannedFiles !== 1 ? 's' : ''}
        </span>
        {(data.staleFiles ?? 0) > 0 && (
          <>
            <span>&middot;</span>
            <span className="text-orange-500 font-medium">
              {data.staleFiles} file{data.staleFiles !== 1 ? 's' : ''} changed since scan
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// --- Sub-components ---

function SubmissionPillsRow({
  submissions,
  submissionId,
  onPick,
  onRescan,
  canRescan,
  rescanBusy,
}: {
  submissions: Submission[];
  submissionId: string | null;
  onPick: (id: string) => void;
  onRescan: () => void;
  canRescan: boolean;
  rescanBusy: boolean;
}) {
  const rescanDisabled = !canRescan || rescanBusy;
  const rescanTitle = rescanBusy
    ? 'Scan in progress'
    : !canRescan
      ? 'Pick an active submission first'
      : 'Re-read unresolved comments + tracked changes from Word';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
        Active subs:
      </span>
      {submissions.map((sub) => {
        const active = sub.id === submissionId;
        return (
          <button
            key={sub.id}
            type="button"
            onClick={() => onPick(sub.id)}
            className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors ${
              active
                ? 'bg-brand-orange text-white shadow-[0_0_0_3px_rgba(236,91,19,0.10)]'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Sub {sub.displaySequence}
          </button>
        );
      })}
      <div className="ml-auto">
        <button
          type="button"
          onClick={onRescan}
          disabled={rescanDisabled}
          title={rescanTitle}
          className={`inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 ${
            rescanDisabled ? 'cursor-not-allowed opacity-50' : ''
          }`}
        >
          {rescanBusy ? (
            <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          {rescanBusy ? 'Scanning…' : 'Rescan'}
        </button>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  subtitle,
  color,
  icon: Icon,
  scopeLines,
}: {
  label: string;
  value: number | string;
  subtitle?: string;
  color: string;
  icon: React.ComponentType<{ className?: string }>;
  /** A2F-491: optional scope breakdown shown below the headline. */
  scopeLines?: { thisSubAndPrior: string; allScopes: string };
}) {
  const colorMap: Record<string, string> = {
    slate: 'text-slate-700',
    green: 'text-green-600',
    orange: 'text-orange-600',
    red: 'text-red-600',
  };
  const iconColorMap: Record<string, string> = {
    slate: 'text-slate-400',
    green: 'text-green-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col justify-between">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={`w-3.5 h-3.5 ${iconColorMap[color] ?? 'text-slate-400'}`} />
        <p className="text-xs text-slate-500">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${colorMap[color] ?? 'text-slate-700'}`}>
        {value}
        {subtitle && <span className="text-sm font-normal text-slate-400">{subtitle}</span>}
      </p>
      {scopeLines && (
        <>
          <p className="text-[10px] text-slate-400 mt-0.5">this sub</p>
          <div className="border-t border-slate-100 mt-2 pt-2 space-y-0.5">
            <p className="text-[10px] text-slate-500 flex items-baseline gap-1.5">
              <span className="font-semibold tabular-nums text-slate-600 min-w-[2rem] text-right">
                {scopeLines.allScopes}
              </span>
              <span className="text-slate-400">all scopes</span>
            </p>
          </div>
        </>
      )}
    </div>
  );
}
